import { AnalyticsService } from '@services/analytics/analytics.service';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { sleep } from '@core/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { UiService } from '../ui/ui.service';
import { TimeInvokingService } from '../time-invoking/time-invoking.service';

export interface ContentPageModel {
  routeUrl: string;
  viewCode: string;
  code: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContentService {
  private contentPage$ = new BehaviorSubject<ContentPageModel>(null);

  private routeUrl: string;

  private viewCode: string;

  private fixedDocumentTitle: string;

  constructor(
    private router: Router,
    private analyticsService: AnalyticsService,
    private uiService: UiService,
    private timeInvokingService: TimeInvokingService
  ) {}

  /**
   * ascolta gli eventi di fine navigazione e imposta il relativo content code
   */
  listenNavigationEnd(): void {
    this.timeInvokingService.invokeOnce(
      'ContentServiceListenNavigationEnd',
      () => {
        this.router.events
          .pipe(
            filter(
              event =>
                event instanceof NavigationEnd && !this.isDummyPage(event.url)
            )
          )
          .subscribe((event: NavigationEnd) => {
            this.updateRouteUrl(event.url);
          });
      }
    );
  }

  /**
   * ascolta i cambiamenti del content page e aggiorna il document title
   */
  listenContentPage(): void {
    this.timeInvokingService.invokeOnce(
      'ContentServiceListenContentPage',
      () => {
        this.getContentPageWhenChange(100).subscribe(async () => {
          // applico ritardo per dare il tempo all'elemento del page title
          // di essere renderizzato (è il tempo dell'animazione)
          await sleep(600);

          this.updateDocumentTitle();
          this.pushPageViewAnalyticsEvent();
        });
      }
    );
  }

  /**
   * restituisce osservabile per i cambiamenti del content code
   */
  getContentPageWhenChange(dueTime: number = 5): Observable<ContentPageModel> {
    return this.contentPage$.asObservable().pipe(
      filter(ev => !!ev),
      debounceTime(dueTime),
      distinctUntilChanged((prev, curr) => prev.code === curr.code)
    );
  }

  /**
   * imposta il nuovo url
   */
  updateRouteUrl(url: string): void {
    this.routeUrl = url;
    this.viewCode = '';
    this.notifyContentPage();
  }

  /**
   * imposta il nuovo view code a partire dalle section
   */
  updateViewCodeOfSections(data: { count: number; current: number }): void {
    if (data.count <= 1) {
      return;
    }

    if (data.current > 0) {
      this.updateViewCode(`${data.current + 1}`);
    } else {
      this.updateViewCode('');
    }
  }

  /**
   * imposta il nuovo view code
   */
  updateViewCode(code: string): void {
    this.viewCode = code;
    this.notifyContentPage();
  }

  /**
   * notifica il content code
   */
  private async notifyContentPage(): Promise<void> {
    this.contentPage$.next({
      viewCode: this.viewCode,
      routeUrl: this.routeUrl,
      code: this.getCode()
    });
  }

  /**
   * crea il codice unico derivato da route url e view code
   */
  private getCode(): string {
    let code = '';

    if (this.routeUrl) {
      code += this.routeUrl
        .split('/')
        .filter(x => !!x)
        .join('_');
    }

    if (this.viewCode) {
      code += '_' + this.viewCode;
    }

    if (code.charAt(0) === '_') {
      code = code.substring(1);
    }

    return code;
  }

  /**
   * aggiorna il document title
   */
  private updateDocumentTitle(): void {
    if (!this.fixedDocumentTitle) {
      this.fixedDocumentTitle = document.title;
    }

    document.title = this.createDocumentTitle();
  }

  /**
   * compone il title composto dalla parte fissa e dalla parte dinamica
   */
  private createDocumentTitle(): string {
    const pageTitle = this.getPageTitle();

    return this.fixedDocumentTitle + (!!pageTitle ? ` - ${pageTitle}` : '');
  }

  /**
   * restituisce il titolo della pagina
   */
  private getPageTitle(): string {
    const content = this.uiService.getTopContent();

    const title = content && content.querySelector('.step-header-title');

    if (title) {
      return title.textContent;
    } else {
      return '';
    }
  }

  /**
   * stabilisce se l'url è della pagina dummy
   */
  private isDummyPage(url: string): boolean {
    return url && url.includes('dummy');
  }

  /**
   * aggiunge l'evento di analytics legato al page view
   */
  private pushPageViewAnalyticsEvent(): void {
    let path = this.routeUrl;

    if (!!this.viewCode) {
      path += `-${this.viewCode}`;
    }

    this.analyticsService.pushPageViewEvent({
      path,
      title: document.title
    });
  }
}
